import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  MailAccountEditForm,
  MailAccountEditInitialValues,
  MailAccountComponents,
  MailAccountAdditionalParams,
  MailAccountFormOptions,
  onMailAccountFormSaved,
  IMailAccountFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedMailAccount, ISharedPatchMailAccount, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchMailAccount, patchMailAccount, getMailAccountUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, useJourneies, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedMailAccount> {}

const MailAccountFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();
  const journeies = useJourneies();

  useEffect(() => {
    fetchMailAccount(id).then(mail_account => setValue(mail_account));
  }, [id, setValue, ]);

  const param_props: IMailAccountFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
    journeies: journeies.journeies,
  };
  const formLayout: IForm | undefined = useMemo(() => value && MailAccountEditForm(value, param_props, t), [value, organizations, journeies, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && MailAccountFormOptions(value, param_props, t), [value, organizations, journeies, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchMailAccount, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchMailAccount) => value && save(() => patchMailAccount(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onMailAccountFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !journeies.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.MailAccount, value)}
    >
      <AtlasForm<ISharedPatchMailAccount>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={MailAccountComponents}
        additionalParams={MailAccountAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={MailAccountEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('MAIL_ACCOUNT')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const MailAccountFormEditView = React.memo(withEdit(MailAccountFormEdit, getMailAccountUpdateSchema));
